/**
 * Custom lightweight-charts series primitive: an up or down caret at the fill
 * price with a bold white quantity label (built-in markers cannot do this layout).
 * Buy points up, sell points down. The caret uses the marker color.
 */

import { timeToXMedia } from './chartMarkersPrimitiveUtils';

const LABEL_COLOR = '#FFFFFF';
const HALO_COLOR = 'rgba(0, 0, 0, 0.72)';
const LABEL_FONT = '700 12px system-ui, -apple-system, sans-serif';
const CARET_W = 8;
const CARET_H = 6;
const CARET_GAP = 4;
const HIT_PADDING = 6;

function pointsUp(marker) {
  if (marker.side === 'sell' || marker.side === 'short') return false;
  if (marker.side === 'buy' || marker.side === 'long') return true;
  const text = String(marker.text ?? '');
  if (text.startsWith('-') || text === 'S') return false;
  return true;
}

function traceCaret(ctx, cx, cy, up) {
  const hw = CARET_W / 2;
  const hh = CARET_H / 2;
  ctx.beginPath();
  if (up) {
    ctx.moveTo(cx, cy - hh);
    ctx.lineTo(cx - hw, cy + hh);
    ctx.lineTo(cx + hw, cy + hh);
  } else {
    ctx.moveTo(cx, cy + hh);
    ctx.lineTo(cx - hw, cy - hh);
    ctx.lineTo(cx + hw, cy - hh);
  }
  ctx.closePath();
}

export class TradeExecutionMarkersPrimitive {
  /** @type {number} Radius in media (CSS) pixels */
  static RADIUS = 13;

  constructor() {
    this._markers = [];
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    this._redrawRaf = 0;
    // Stable view + renderer. lightweight-charts caches these by identity, so the
    // draw function must read this._markers at paint time. A new renderer object
    // per update can keep the previous frame's markers on screen.
    this._renderer = {
      draw: (target) => this._draw(target),
    };
    this._paneView = {
      zOrder: () => 'top',
      update: () => {},
      renderer: () => (this._markers.length ? this._renderer : null),
    };
    this._paneViews = [this._paneView];
  }

  updateAllViews() {
    this._paneView.update();
  }

  /**
   * @param {Array<{ time: number, price: number, color: string, text: string, id?: string, side?: string }>} markers
   */
  setMarkers(markers) {
    this._markers = Array.isArray(markers) ? markers.slice() : [];
    this._scheduleRedraw();
  }

  _scheduleRedraw() {
    this._requestUpdate?.();
    // requestUpdate only queues one frame. If that frame was already committed
    // before these markers were stored, schedule another so the eye toggle paints.
    if (typeof requestAnimationFrame !== 'function') return;
    if (this._redrawRaf) cancelAnimationFrame(this._redrawRaf);
    this._redrawRaf = requestAnimationFrame(() => {
      this._redrawRaf = 0;
      this._requestUpdate?.();
    });
  }

  /** @param {import('lightweight-charts').SeriesAttachedParameter} param */
  attached(param) {
    this._chart = param.chart;
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
    this._scheduleRedraw();
  }

  detached() {
    if (this._redrawRaf) {
      cancelAnimationFrame(this._redrawRaf);
      this._redrawRaf = 0;
    }
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    this._markers = [];
  }

  paneViews() {
    return this._paneViews;
  }

  /**
   * @param {number} x
   * @param {number} y
   * @returns {import('lightweight-charts').PrimitiveHoveredItem | null}
   */
  hitTest(x, y) {
    const chart = this._chart;
    const series = this._series;
    if (!chart || !series || !this._markers.length) return null;
    const halfH = TradeExecutionMarkersPrimitive.RADIUS;
    for (let i = this._markers.length - 1; i >= 0; i--) {
      const m = this._markers[i];
      if (m.id == null) continue;
      const xm = timeToXMedia(chart, m.time);
      const ym = series.priceToCoordinate(m.price);
      if (xm == null || ym == null) continue;
      const halfW = (CARET_W + CARET_GAP + String(m.text ?? '').length * 8) / 2 + HIT_PADDING;
      const dx = x - xm;
      const dy = y - ym;
      if (Math.abs(dx) <= halfW && Math.abs(dy) <= halfH) {
        return { externalId: String(m.id), zOrder: 'top' };
      }
    }
    return null;
  }

  _draw(target) {
    const chart = this._chart;
    const series = this._series;
    const markers = this._markers;
    if (!chart || !series || !markers.length) return;

    target.useMediaCoordinateSpace(({ context: ctx }) => {
      ctx.font = LABEL_FONT;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.lineJoin = 'round';

      for (const m of markers) {
        const x = timeToXMedia(chart, m.time);
        const y = series.priceToCoordinate(m.price);
        if (x == null || y == null) continue;

        const label = m.text ?? '';
        const textWidth = ctx.measureText(label).width;
        const total = CARET_W + (label ? CARET_GAP + textWidth : 0);
        const left = x - total / 2;
        const caretCx = left + CARET_W / 2;
        const up = pointsUp(m);

        traceCaret(ctx, caretCx, y, up);
        ctx.strokeStyle = HALO_COLOR;
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.fillStyle = m.color || LABEL_COLOR;
        ctx.fill();

        if (!label) continue;
        const textX = left + CARET_W + CARET_GAP;
        ctx.lineWidth = 3;
        ctx.strokeStyle = HALO_COLOR;
        ctx.strokeText(label, textX, y);
        ctx.fillStyle = LABEL_COLOR;
        ctx.fillText(label, textX, y);
      }
    });
  }
}
